import React, { useState, useMemo } from 'react';
import { FaTimes, FaSave, FaWhatsapp, FaSpinner, FaExclamationTriangle, FaPlus } from 'react-icons/fa';
import { supabase } from '../../utils/supabaseClient';
import { authFetch } from '../../utils/authFetch';
import '../../styles/EditTransactionModal.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://krmmadhubanibackend.onrender.com';

/**
 * Compute the total due (the amount the student should have paid this transaction)
 * Mirrors the formula in Fees.js calculateTotalDue.
 */
const computeTotalDue = (feeDues, monthly, exam, transport, discount) => {
  const prev = parseFloat(feeDues) || 0;
  const m = parseFloat(monthly) || 0;
  const e = parseFloat(exam) || 0;
  const t = parseFloat(transport) || 0;
  const d = parseFloat(discount) || 0;
  if (prev < 0) {
    return Math.abs(prev) + m + e + t - d;
  }
  return m + e + t - prev - d;
};

const EditTransactionModal = ({ transaction, onClose, onSaved, onNotify }) => {
  // Discount is stored as a NEGATIVE number in DB (e.g. -200). Show positive in UI.
  const initialDiscountPositive = Math.abs(parseFloat(transaction.discount) || 0);

  // Format created_at as YYYY-MM-DD for date input
  const formatDateInput = (dateStr) => {
    if (!dateStr) return new Date().toISOString().split('T')[0];
    return new Date(dateStr).toISOString().split('T')[0];
  };

  const [form, setForm] = useState({
    fee_dues: transaction.fee_dues || 0,
    current_month_fee: transaction.current_month_fee || 0,
    transport_fee: transaction.transport_fee || 0,
    examination_fee: transaction.examination_fee || 0,
    discount: initialDiscountPositive,
    amount_paid: transaction.amount_paid || 0,
    payment_method: transaction.payment_method || 'Cash',
    created_at: formatDateInput(transaction.created_at)
  });

  const [otherFees, setOtherFees] = useState(
    Array.isArray(transaction.other) ? transaction.other.map(f => ({ label: f.label || '', value: f.value || 0 })) : []
  );

  const [sendToWhatsApp, setSendToWhatsApp] = useState(false);
  const [saving, setSaving] = useState(false);

  // Live-computed totals
  const otherFeesTotal = useMemo(
    () => otherFees.reduce((sum, f) => sum + (parseFloat(f.value) || 0), 0),
    [otherFees]
  );
  const totalDue = useMemo(
    () => Math.max(0, computeTotalDue(form.fee_dues, form.current_month_fee, form.examination_fee, form.transport_fee, 0) + otherFeesTotal - form.discount),
    [form, otherFeesTotal]
  );
  const newDues = useMemo(
    () => (parseFloat(form.amount_paid) || 0) - totalDue,
    [form.amount_paid, totalDue]
  );

  // Did the resulting balance change vs the original transaction?
  const originalNewDues = parseFloat(transaction.new_dues) || 0;
  const balanceChanged = Math.round(newDues) !== Math.round(originalNewDues);

  const handleField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  const handleNumberField = (key, value) => {
    // Allow empty string while typing; coerce on save
    if (value === '' || value === '-') {
      setForm(prev => ({ ...prev, [key]: value }));
      return;
    }
    const num = parseFloat(value);
    setForm(prev => ({ ...prev, [key]: isNaN(num) ? 0 : num }));
  };

  const sendReceiptWhatsApp = async (txnId, txnRow) => {
    // Fetch student + private details for phone numbers and receipt URL params
    const { data: student } = await supabase
      .from('students')
      .select('Rno, Name, Class')
      .eq('Rno', transaction.student_rno)
      .single();

    const { data: priv } = await supabase
      .from('students_private_details')
      .select('Mobile1, Mobile2, "Father\'s Name (Son)", "Mother\'s Name (Son)"')
      .eq('Rno', transaction.student_rno)
      .single();

    if (!priv || (!priv.Mobile1 && !priv.Mobile2)) {
      onNotify?.('No phone numbers on file — receipt not sent.', 'warning');
      return;
    }

    const dues = txnRow.new_dues < 0 ? Math.abs(txnRow.new_dues) : 0;
    const advance = txnRow.new_dues > 0 ? txnRow.new_dues : 0;

    const studentName = encodeURIComponent(student?.Name || transaction.student_name || '');
    const rollNumber = encodeURIComponent(transaction.student_rno);
    const studentClass = encodeURIComponent(student?.Class || transaction.student_class || 'N/A');
    const fatherName = encodeURIComponent(priv["Father's Name (Son)"] || 'N/A');
    const motherName = encodeURIComponent(priv["Mother's Name (Son)"] || 'N/A');
    const receiptUrl = `academic/transaction/${txnId}?name=${studentName}&rno=${rollNumber}&class=${studentClass}&father=${fatherName}&mother=${motherName}`;

    const phones = [];
    if (priv.Mobile1) phones.push({ number: `91${priv.Mobile1}`, label: 'Mobile1' });
    if (priv.Mobile2) phones.push({ number: `91${priv.Mobile2}`, label: 'Mobile2' });

    let success = 0;
    for (const p of phones) {
      try {
        const res = await authFetch(`${API_BASE_URL}/api/whatsapp/send-payment-confirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            phoneNumber: p.number,
            amount: Math.round(txnRow.amount_paid),
            studentName: student?.Name || transaction.student_name || '',
            dues,
            advance,
            receiptUrl
          })
        });
        if (res.ok) success++;
        if (phones.length > 1) await new Promise(r => setTimeout(r, 500));
      } catch (err) {
        console.error('WhatsApp send error:', err);
      }
    }

    if (success > 0) {
      onNotify?.(
        phones.length > 1
          ? `Receipt resent to ${success} WhatsApp number(s).`
          : 'Receipt resent to WhatsApp.',
        'success'
      );
    } else {
      onNotify?.('Failed to send receipt to WhatsApp.', 'error');
    }
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);

    try {
      const monthly = parseFloat(form.current_month_fee) || 0;
      const transport = parseFloat(form.transport_fee) || 0;
      const exam = parseFloat(form.examination_fee) || 0;
      const discountPositive = parseFloat(form.discount) || 0;
      const amountPaid = parseFloat(form.amount_paid) || 0;
      const feeDuesVal = parseFloat(form.fee_dues) || 0;

      const updatePayload = {
        fee_dues: Math.round(feeDuesVal),
        current_month_fee: Math.round(monthly),
        transport_fee: Math.round(transport),
        examination_fee: Math.round(exam),
        discount: discountPositive > 0 ? -Math.round(discountPositive) : 0,
        other: otherFees.filter(f => f.label && f.value).map(f => ({ label: f.label, value: Math.round(parseFloat(f.value) || 0) })),
        total_amount: Math.round(totalDue),
        amount_paid: Math.round(amountPaid),
        new_dues: Math.round(newDues),
        payment_method: form.payment_method,
        created_at: form.created_at ? new Date(form.created_at).toISOString() : undefined
      };

      const { data: updated, error } = await supabase
        .from('payment_transactions')
        .update(updatePayload)
        .eq('id', transaction.id)
        .select()
        .single();

      if (error) throw error;

      // Auto-sync student's FeeDues if balance changed
      if (balanceChanged) {
        const { error: studentError } = await supabase
          .from('students_private_details')
          .update({ FeeDues: Math.round(newDues) })
          .eq('Rno', transaction.student_rno);
        if (studentError) {
          console.error('Failed to sync student FeeDues:', studentError);
        } else {
          console.log(`✅ Synced student FeeDues to ₹${Math.round(newDues)}`);
        }
      }

      onNotify?.('Transaction updated successfully.', 'success');

      if (sendToWhatsApp) {
        await sendReceiptWhatsApp(transaction.id, updated || updatePayload);
      }

      onSaved?.();
      onClose?.();
    } catch (err) {
      console.error('Update transaction error:', err);
      onNotify?.(`Failed to update: ${err.message || 'Unknown error'}`, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="etm-overlay" onClick={onClose}>
      <div className="etm-modal" onClick={e => e.stopPropagation()}>
        <div className="etm-header">
          <div className="etm-header-left">
            <h3>Edit Transaction</h3>
            <span className="etm-subtitle">
              {transaction.student_name} · Roll {transaction.student_rno} · Class {transaction.student_class}
            </span>
          </div>
          <button className="etm-close" onClick={onClose} disabled={saving}>
            <FaTimes />
          </button>
        </div>

        <div className="etm-body">
          <div className="etm-grid">
            <div className="etm-field">
              <label>Previous Dues (₹)</label>
              <input
                type="number"
                value={form.fee_dues}
                onChange={e => handleNumberField('fee_dues', e.target.value)}
                disabled={saving}
              />
              <span className="etm-hint">Negative = student owed money before this txn</span>
            </div>

            <div className="etm-field">
              <label>Monthly Fee (₹)</label>
              <input
                type="number"
                min="0"
                value={form.current_month_fee}
                onChange={e => handleNumberField('current_month_fee', e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="etm-field">
              <label>Transport Fee (₹)</label>
              <input
                type="number"
                min="0"
                value={form.transport_fee}
                onChange={e => handleNumberField('transport_fee', e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="etm-field">
              <label>Examination Fee (₹)</label>
              <input
                type="number"
                min="0"
                value={form.examination_fee}
                onChange={e => handleNumberField('examination_fee', e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="etm-field">
              <label>Discount (₹)</label>
              <input
                type="number"
                min="0"
                value={form.discount}
                onChange={e => handleNumberField('discount', e.target.value)}
                disabled={saving}
              />
              <span className="etm-hint">Enter as a positive number; stored as negative</span>
            </div>

            <div className="etm-field">
              <label>Transaction Date</label>
              <input
                type="date"
                value={form.created_at}
                min={`${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`}
                onChange={e => handleField('created_at', e.target.value)}
                disabled={saving}
              />
            </div>
          </div>

          {/* Other Fees */}
          <div className="etm-other-fees">
            <div className="etm-other-fees-header">
              <label>Other Fees</label>
              <button
                type="button"
                className="etm-other-add-btn"
                onClick={() => setOtherFees([...otherFees, { label: '', value: '' }])}
                disabled={saving}
                title="Add other fee"
              >
                <FaPlus />
              </button>
            </div>
            {otherFees.map((fee, idx) => (
              <div className="etm-other-row" key={idx}>
                <input
                  type="text"
                  className="etm-other-label"
                  placeholder="Label"
                  value={fee.label}
                  onChange={(e) => {
                    const updated = [...otherFees];
                    updated[idx] = { ...updated[idx], label: e.target.value };
                    setOtherFees(updated);
                  }}
                  disabled={saving}
                />
                <div className="etm-other-value-wrap">
                  <span className="etm-rupee">₹</span>
                  <input
                    type="number"
                    placeholder="0"
                    value={fee.value}
                    onChange={(e) => {
                      const updated = [...otherFees];
                      updated[idx] = { ...updated[idx], value: e.target.value };
                      setOtherFees(updated);
                    }}
                    disabled={saving}
                  />
                </div>
                <button
                  type="button"
                  className="etm-other-remove-btn"
                  onClick={() => setOtherFees(otherFees.filter((_, i) => i !== idx))}
                  disabled={saving}
                  title="Remove"
                >
                  <FaTimes />
                </button>
              </div>
            ))}
          </div>

          <div className="etm-grid">
            <div className="etm-field">
              <label>Amount Paid (₹)</label>
              <input
                type="number"
                min="0"
                value={form.amount_paid}
                onChange={e => handleNumberField('amount_paid', e.target.value)}
                disabled={saving}
              />
            </div>

            <div className="etm-field">
              <label>Payment Method</label>
              <select
                value={form.payment_method}
                onChange={e => handleField('payment_method', e.target.value)}
                disabled={saving}
              >
                <option value="Cash">Cash</option>
                <option value="Online">Online</option>
              </select>
            </div>
          </div>

          {/* Live computed summary */}
          <div className="etm-summary">
            <div className="etm-summary-row">
              <span>Total Due (computed):</span>
              <strong>₹{Math.round(totalDue)}</strong>
            </div>
            <div className="etm-summary-row">
              <span>New Dues (computed):</span>
              <strong className={newDues < 0 ? 'etm-neg' : newDues > 0 ? 'etm-pos' : ''}>
                ₹{Math.abs(Math.round(newDues))}
                {newDues < 0 ? ' (Due)' : newDues > 0 ? ' (Advance)' : ''}
              </strong>
            </div>
            {balanceChanged && (
              <div className="etm-warn">
                <FaExclamationTriangle />
                <span>
                  New Dues changed from <strong>₹{Math.abs(Math.round(originalNewDues))}</strong> to <strong>₹{Math.abs(Math.round(newDues))}</strong>.
                  The student's FeeDues will be synced automatically on save.
                </span>
              </div>
            )}
          </div>

          {/* WhatsApp resend option */}
          <div className="etm-whatsapp">
            <label className="etm-checkbox">
              <input
                type="checkbox"
                checked={sendToWhatsApp}
                onChange={e => setSendToWhatsApp(e.target.checked)}
                disabled={saving}
              />
              <FaWhatsapp className="etm-wa-icon" />
              <span>Send receipt to WhatsApp after saving</span>
            </label>
            <span className="etm-hint">Resends the updated receipt to Mobile1 & Mobile2 on file</span>
          </div>
        </div>

        <div className="etm-footer">
          <button className="etm-btn etm-btn-cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="etm-btn etm-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? <><FaSpinner className="etm-spin" /> Saving...</> : <><FaSave /> Save Changes</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTransactionModal;
